import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  Save as SaveIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { addressService } from '../../services/addressService';
import { authAPI } from '../../services/api';
import { formatFullName } from '../../utils/nameUtils';

const MyShippingAddress = ({ currentUser }) => {
  const [addresses, setAddresses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Address form state
  const [addressForm, setAddressForm] = useState({
    name: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    isDefault: false,
    label: '' // Custom label like "Home", "Office", etc.
  });

  // Load saved shipping addresses and sync with profile
  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    const savedAddresses = addressService.getAddresses();
    
    // If no addresses exist, try to create one from user profile
    if (savedAddresses.length === 0) {
      try {
        const response = await authAPI.getCurrentUser();
        if (response.success && response.data) {
          const user = response.data;
          const profile = user.profile || {};
          
          // If user has address info in profile, create default address
          if (profile.address || profile.city || profile.state || profile.zipCode) {
            const fullName = formatFullName(user);
            const profileAddress = {
              name: fullName || 'My Address',
              company: profile.clinicName || '',
              address1: profile.address || '',
              address2: '',
              city: profile.city || '',
              state: profile.state || '',
              zipCode: profile.zipCode || '',
              phone: user.phone || '',
              isDefault: true,
              label: 'Default Address'
            };
            
            // Save profile address as default
            const result = addressService.saveAddress(profileAddress);
            if (result.success) {
              setAddresses([result.address]);
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error loading user profile for address:', error);
      }
    }
    
    setAddresses(savedAddresses);
  };

  const resetForm = () => {
    setAddressForm({
      name: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      isDefault: false,
      label: ''
    });
    setErrors({});
  };

  const handleAddAddress = () => {
    resetForm();
    setEditingAddress(null);
    setDialogOpen(true);
  };

  const handleEditAddress = (address) => {
    setAddressForm(address);
    setEditingAddress(address);
    setDialogOpen(true);
  };

  const handleDeleteAddress = (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      const result = addressService.deleteAddress(addressId);
      if (result.success) {
        loadAddresses();
      }
    }
  };

  const handleSetDefault = (addressId) => {
    const result = addressService.setDefaultAddress(addressId);
    if (result.success) {
      loadAddresses();
    }
  };

  const validateAddress = () => {
    const newErrors = {};
    
    if (!addressForm.name.trim()) newErrors.name = 'Name is required';
    if (!addressForm.address1.trim()) newErrors.address1 = 'Street address is required';
    if (!addressForm.city.trim()) newErrors.city = 'City is required';
    if (!addressForm.state.trim()) newErrors.state = 'State is required';
    if (!addressForm.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    if (addressForm.zipCode && !/^\d{5}(-\d{4})?$/.test(addressForm.zipCode)) {
      newErrors.zipCode = 'Invalid ZIP code format';
    }
    if (!addressForm.phone.trim()) newErrors.phone = 'Phone is required';
    if (addressForm.phone && !/^\d{10}$/.test(addressForm.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Invalid phone format (10 digits required)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateAddress()) return;

    try {
      setLoading(true);
      setSuccess(false);

      let result;
      if (editingAddress) {
        result = addressService.updateAddress(editingAddress.id, addressForm);
      } else {
        result = addressService.saveAddress(addressForm);
      }

      if (result.success) {
        loadAddresses();
        setDialogOpen(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setErrors({ general: result.error || 'Failed to save address' });
      }

    } catch (err) {
      console.error('Error saving address:', err);
      setErrors({ general: 'Failed to save address. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setAddressForm({ ...addressForm, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          My Addresses
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          Manage multiple addresses for faster checkout.
        </Typography>
      </Box>

      {/* Add New Address Button */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddAddress}
          sx={{
            bgcolor: '#0d9488',
            '&:hover': { bgcolor: '#0f766e' },
            px: 3,
            py: 1.5,
            fontWeight: 600
          }}
        >
          Add New Address
        </Button>
      </Box>

      {/* Addresses List */}
      <Grid container spacing={3}>
        {addresses.map((address) => (
          <Grid item xs={12} md={6} key={address.id}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                border: address.isDefault ? '2px solid #0d9488' : '1px solid #e5e7eb',
                position: 'relative'
              }}
            >
              {address.isDefault && (
                <Chip
                  icon={<StarIcon />}
                  label="Default"
                  size="small"
                  color="success"
                  sx={{ position: 'absolute', top: 12, right: 12 }}
                />
              )}
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {address.label || 'Address'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {address.name}
                </Typography>
                {address.company && (
                  <Typography variant="body2" color="text.secondary">
                    {address.company}
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" sx={{ mb: 1 }}>
                {address.address1}
                {address.address2 && <>, {address.address2}</>}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {address.city}, {address.state} {address.zipCode}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {address.phone}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {!address.isDefault && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSetDefault(address.id)}
                  >
                    Set Default
                  </Button>
                )}
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => handleEditAddress(address)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => handleDeleteAddress(address.id)}
                  disabled={addresses.length === 1} // Can't delete last address
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        ))}
        
        {addresses.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#f8fafc' }}>
              <HomeIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No Addresses Added
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add an address to make checkout faster and easier.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddAddress}
                sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}
              >
                Add Address
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Address Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAddress ? 'Edit Address' : 'Add New Address'}
        </DialogTitle>
        <DialogContent>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Label (Optional)"
                value={addressForm.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                placeholder="Home, Office, Clinic, etc."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={addressForm.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company (Optional)"
                value={addressForm.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={addressForm.address1}
                onChange={(e) => handleInputChange('address1', e.target.value)}
                error={!!errors.address1}
                helperText={errors.address1}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Apartment, Suite, etc. (Optional)"
                value={addressForm.address2}
                onChange={(e) => handleInputChange('address2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={addressForm.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                error={!!errors.city}
                helperText={errors.city}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                value={addressForm.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                error={!!errors.state}
                helperText={errors.state}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={addressForm.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                error={!!errors.zipCode}
                helperText={errors.zipCode}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={addressForm.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                placeholder="(555) 123-4567"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addressForm.isDefault}
                    onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                  />
                }
                label="Set as default shipping address"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}
          >
            {loading ? 'Saving...' : editingAddress ? 'Update Address' : 'Save Address'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyShippingAddress;
